import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import axios from "axios";
import clsx from "clsx";
import { toast } from "react-toastify";
import VendorItemLinkModalBody from "views/vendors/VendorBody/VendorItems/VendorItemLinkModal/VendorItemLinkModalBody";

export interface IPurchaseOrderItem {
    item_id: number | null;
    inventory_item_id: number | null;
    vendor_item_id: number | null;
    vendor_part_number: string | null;
    vendor_id: number | null;
    item_name: string;
    quantity: number;
    price: number | null;
    latest_status: string | null;
    last_update: string | null;
    vendor: string | null;
}

export interface IConnectionStatus {
    access_token: string;
    created_at: string;
    credentials_exist: boolean;
    branches: {
        id: string;
        branch_name: string;
        branch_code: string;
        is_default: boolean;
    }[];
}

export interface IBranch {
    id: string;
    branch_name: string;
    branch_code: string;
    is_default: boolean;
    ship_to_sequence_number: string;
    address_line1: string | null;
    branch_city_state_zip: string | null;
    branch_phone: string | null;
    business_hours: string | null;
}

interface IProps {
    orderId: number;
    sendViaEmail: boolean;
    setSendViaEmail: (value: boolean) => void;
    sendViaApi: boolean;
    setSendViaApi: (value: boolean) => void;
    onSuccess?: () => void;
}

interface IBusiness {
    id: string;
    name: string;
    address: string | null;
    city: string | null;
    state: string | null;
    zipcode: string | null;
    default_timezone: string | null;
}

interface IInventoryManager {
    user_id: string;
    first_name: string;
    last_name: string;
    email: string | null;
    is_inventory_manager: string;
    phone: string | null;
}

const SendPurchaseOrder = ({
    orderId,
    sendViaEmail,
    setSendViaEmail,
    sendViaApi,
    setSendViaApi,
    onSuccess,
}: IProps) => {
    const queryClient = useQueryClient();

    const [isSending, setIsSending] = useState(false);
    const [isCheckingStock, setIsCheckingStock] = useState(false);
    const [unlinkedItems, setUnlinkedItems] = useState<IPurchaseOrderItem[]>([]);
    const [linkedCount, setLinkedCount] = useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
    const [selectedItemForLink, setSelectedItemForLink] = useState<IPurchaseOrderItem | null>(null);

    const { data, isLoading, error } = useQuery<{
        items: IPurchaseOrderItem[];
        connection: IConnectionStatus;
        branch: IBranch;
        business: IBusiness;
        inventoryManager: IInventoryManager
    }>(
        ["po-items", orderId],
        () =>
            axios
                .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/purchase-order-items`, {
                    params: { orderId },
                })
                .then((res) => ({
                    items: res.data.items.map((item: any) => ({
                        item_id: item.item_id ? parseInt(item.item_id) : null,
                        vendor_item_id: item.vendor_item_id ? parseInt(item.vendor_item_id) : null,
                        vendor_id: item.vendor_id ? parseInt(item.vendor_id) : null,
                        item_name: item.item_name,
                        quantity: parseFloat(item.quantity),
                        price: item.price != null ? item.price / 100 : null,
                        latest_status: item.latest_status,
                        last_update: item.last_update,
                        vendor: item.vendor ?? null,
                        inventory_item_id: item.inventory_item_id ? parseInt(item.inventory_item_id) : null,
                        vendor_part_number: item.vendor_part_number ?? null,
                    })),
                    connection: res.data.connection,
                    branch: res.data.branch,
                    business: res.data.business,
                    inventoryManager: res.data.inventoryManager,
                })),
        {
            enabled: sendViaApi,
        }
    );

    useEffect(() => {
        if (data?.items) {
            const items = data.items;
            const unlinked = items.filter(item => !item.vendor_part_number);
            setUnlinkedItems(unlinked);
            setLinkedCount(items.length - unlinked.length);
            setTotalItems(items.length);
        }
    }, [data]);

    const toggleEmail = () => {
        setSendViaEmail(!sendViaEmail);
    };

    const toggleApi = () => {
        setSendViaApi(!sendViaApi);
    };

    const handleSend = async () => {
        if (unlinkedItems.length > 0 && sendViaApi) {
            toast.warn(`Cannot send: The following items are not linked (missing vendor part number): ${unlinkedItems.map(i => i.item_name).join(", ")}. Link them in Vendors.`);
            return;
        }

        setIsSending(true);
        if (sendViaApi) {
            setIsCheckingStock(true);
            try {
                const availRes = await axios.post(
                    `${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/check-availability`,
                    {
                        items: data?.items.map(item => ({
                            heritagePartNumber: item.vendor_part_number,
                            customerPartNumber: item.vendor_part_number || item.item_name, // Fallback
                            uom: "EA",
                        })),
                    }
                );

                const availData = availRes.data.available;
                const stockIssues: string[] = [];
                availData.products.forEach((prod: any, idx: number) => {
                    const item = data?.items[idx];
                    const inv = prod.inventory?.[0]?.uom_inventory?.[0];
                    const availQty = parseFloat(inv?.available_inventory || "0");
                    if (availQty < (item?.quantity ?? 0) || inv?.message?.includes("not available")) {
                        stockIssues.push(`${item?.item_name}: ${availQty > 0 ? `Only ${availQty} available (need ${item?.quantity ?? 0})` : "Unavailable"}`);
                    }
                });

                if (stockIssues.length > 0) {
                    toast.error(`Stock issues: ${stockIssues.join("; ")}. Cannot submit.`);
                    setIsSending(false);
                    setIsCheckingStock(false);
                    return;
                }
            } catch (err) {
                toast.error("Failed to check stock levels.");
                setIsSending(false);
                setIsCheckingStock(false);
                return;
            }
            setIsCheckingStock(false);
        }

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/purchasing/send`,
                {
                    orderId,
                    sendViaEmail,
                    sendViaApi,
                }
            );

            if (response.status === 200) {
                toast.success("Purchase order sent successfully");
                if (onSuccess) onSuccess();
            } else {
                toast.error("Failed to send purchase order");
            }
        } catch (error) {
            toast.error("Error sending purchase order");
            console.error(error);
        } finally {
            setIsSending(false);
        }
        setIsSending(false); // Still reset sending state
    };

    const handleRefreshPrice = async (vendorItemId: string) => {
        try {
            toast.info("Refreshing price...");

            const res = await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/heritage/refresh-item`, {
                itemId: vendorItemId,
            });

            const updated = res.data?.updatedItem;
            const priceCents = updated?.price;
            const price = priceCents != null ? (priceCents / 100).toFixed(2) : null;

            if (res.status === 200) {
                if (price) {
                    toast.success(`Price refreshed to $${price}`);
                } else {
                    toast.warn("Price unavailable – item price was unset.");
                }

                // Optionally refetch the query so the UI updates
                queryClient.invalidateQueries(["po-items", orderId]);
            } else {
                toast.warn("No price returned");
            }
        } catch {
            toast.error("Failed to refresh price");
        }
    };

    const handleOpenLinkModal = (item: IPurchaseOrderItem) => {
        setSelectedItemForLink(item);
        setIsLinkModalOpen(true);
    };

    const handleLinkSuccess = () => {
        queryClient.invalidateQueries(["po-items", orderId]);
        setIsLinkModalOpen(false);
    };

    return (
        <div className="bg-white p-4 rounded shadow-md w-96 text-sm">

            <div className="flex justify-between items-start mb-1">
                <h3 className="text-lg font-semibold">Send Purchase Order</h3>

            </div>

            <div className="flex justify-between items-center mb-2">
                <span>Send via Email</span>
                <button
                    className={clsx(
                        "w-10 h-5 rounded-full flex items-center px-1 transition-colors",
                        sendViaEmail ? "bg-green-500" : "bg-gray-300"
                    )}
                    onClick={toggleEmail}
                >
                    <div
                        className={clsx(
                            "bg-white w-4 h-4 rounded-full shadow transition-transform",
                            sendViaEmail ? "translate-x-5" : "translate-x-0"
                        )}
                    />
                </button>
            </div>

            <div className="flex justify-between items-center mb-2">
                <span>Send via API </span>
                <button
                    className={clsx(
                        "w-10 h-5 rounded-full flex items-center px-1 transition-colors",
                        sendViaApi ? "bg-green-500" : "bg-gray-300"
                    )}
                    onClick={toggleApi}
                >
                    <div
                        className={clsx(
                            "bg-white w-4 h-4 rounded-full shadow transition-transform",
                            sendViaApi ? "translate-x-5" : "translate-x-0"
                        )}
                    />
                </button>
            </div>


            {sendViaApi && (
                <div className="border-t pt-3 mt-3 space-y-2">

                    <div className="flex justify-between items-center">
                        <h4 className="font-medium text-gray-700">Connection:</h4>
                        <div className="text-xs text-gray-500">
                            {data?.connection?.credentials_exist && data?.connection?.access_token ? (
                                <span className="text-green-500">Connected</span>
                            ) : (
                                <span className="text-red-500">Not Connected</span>
                            )}
                        </div>
                    </div>


                    <h4 className="font-medium text-gray-700">Vendor Items</h4>
                    {isLoading && <p className="text-gray-500">Loading items...</p>}
                    {error && <p className="text-red-500">Failed to load items</p>}
                    <div className="max-h-60 overflow-y-auto border rounded bg-gray-50 divide-y">
                        {!isLoading &&
                            data?.items?.map((item, i) => (
                                <div className={clsx("flex items-start justify-between px-3 py-2", !item.vendor_part_number && "bg-red-50")}>
                                    <div className="flex-1 pr-2">
                                        <div className="font-medium flex items-center gap-1">
                                            {item.item_name}
                                            {!item.vendor_part_number && (
                                                <button
                                                    className="text-red-500 text-xs font-semibold hover:underline"
                                                    onClick={() => handleOpenLinkModal(item)}
                                                >
                                                    (Not Linked)
                                                </button>
                                            )}
                                        </div>
                                        <div className="text-xs text-gray-500">Vendor Item ID: {item.vendor_item_id ?? <span className="text-red-500">N/A</span>}</div>
                                        <div className="text-xs text-gray-500">Inventory ID: {item.item_id}</div>
                                        <div className="text-xs text-gray-500">Vendor Part #: {item.vendor_part_number ?? <span className="text-red-500">N/A</span>}</div>
                                        {/* <div className="text-xs text-gray-500">Vendor ID: {item.vendor_id ?? <span className="text-red-500">N/A</span>}</div> */}
                                        <div className="text-xs text-gray-500">Qty: {item.quantity}</div>
                                    </div>

                                    <div className="text-right text-xs min-w-[120px]">
                                        <div className="flex items-center justify-end gap-1">
                                            <strong>Price:</strong>{" "}
                                            {item.price != null ? (
                                                `$${item.price.toFixed(2)}`
                                            ) : (
                                                <>
                                                    <span className="text-red-500">X</span>
                                                    <a
                                                        href="/vendors"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="ml-1 text-blue-600 hover:underline"
                                                        title="Go to Vendors"
                                                    >
                                                        🔗
                                                    </a>
                                                </>
                                            )}
                                            {item.vendor_item_id && (
                                                <button
                                                    onClick={() => handleRefreshPrice(item.vendor_item_id!.toString())}
                                                    className="text-blue-500 text-[11px] hover:underline"
                                                >
                                                    Refresh
                                                </button>
                                            )}
                                        </div>
                                        <div>Status: {item.latest_status ?? "N/A"}</div>
                                        <div>
                                            <span className="text-gray-500">
                                                {item.last_update ? new Date(item.last_update).toLocaleDateString() : "N/A"}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                    </div>

                    {sendViaApi && data?.branch && (
                        <div className="border rounded px-3 py-2 bg-gray-50 text-xs text-gray-700">
                            <div className="font-semibold text-gray-800 mb-1">Sending to:</div>
                            <div><strong>{data.branch.branch_name}</strong> ({data.branch.branch_code})</div>
                            <div>{data.branch.address_line1}, {data.branch.branch_city_state_zip}</div>
                            <div>{data.branch.branch_phone}</div>
                            <div className="italic">{data.branch.business_hours}</div>
                        </div>
                    )}

                    {sendViaApi && data?.business && (
                        <div className="border rounded px-3 py-2 bg-gray-50 text-xs text-gray-700">
                            <div className="font-semibold text-gray-800 mb-1">Ship To:</div>
                            <div><strong>{data.business.name}</strong></div>
                            <div>{data.business.address}, {data.business.city}, {data.business.state} {data.business.zipcode}</div>
                            <div>C/O: {data.inventoryManager.first_name} {data.inventoryManager.last_name}</div>
                            <div>{data.inventoryManager.phone}</div>
                            <div>{data.inventoryManager.email}</div>
                        </div>
                    )}


                </div>
            )}

            <div className="flex justify-between items-center gap-2 pt-4 mt-4 border-t">
                {sendViaApi && totalItems > 0 && (
                    <div className="text-xs font-medium">
                        <span className={clsx(linkedCount === totalItems ? "text-green-600" : "text-red-600")}>
                            {linkedCount === totalItems ? "✔ " : "⚠ "} {linkedCount}/{totalItems} items linked
                        </span>
                    </div>
                )}
                <div className="flex gap-2">
                    <button
                        onClick={() => {
                            setSendViaApi(false);
                            setSendViaEmail(false);
                        }}
                        className="px-4 py-1.5 rounded border text-gray-700 hover:bg-gray-100"
                    >
                        Cancel
                    </button>

                    <button
                        onClick={handleSend}
                        disabled={isSending || (sendViaApi && unlinkedItems.length > 0)}
                        className="px-4 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                        {isCheckingStock ? "Checking Stock Levels..." : isSending ? "Sending..." : "Send"}
                    </button>
                </div>
            </div>

            {isLinkModalOpen && selectedItemForLink && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-4 rounded shadow-md w-3/4 h-3/4 overflow-auto relative">
                        <button
                            className="absolute top-2 right-2 text-gray-600 hover:text-gray-800"
                            onClick={() => setIsLinkModalOpen(false)}
                        >
                            Close
                        </button>
                        <VendorItemLinkModalBody
                            vendorId={11} // Hardcoded for testing
                            isHeritageVendor={true}
                            isSiteoneVendor={false}
                            preSelectedItemId={selectedItemForLink.inventory_item_id ?? selectedItemForLink.item_id ?? undefined}
                            onLinkSuccess={handleLinkSuccess}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

export default SendPurchaseOrder;